import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import { formatHeat } from "@/utils/format";

const KEYWORDS = [
  "大结局改编",
  "旗舰机跳水",
  "推荐机制",
  "音乐节退票",
  "独立游戏",
  "最低价承诺",
  "判罚争议",
  "城市文旅",
];

export default function HotBoard({ topics }: { topics: Topic[] }) {
  const maxHeat = topics[0]?.heat ?? 1;

  return (
    <section id="hot-board" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="font-label text-xs tracking-[0.28em] text-primary-600">
              HOT BOARD
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              今日热议榜单
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              按讨论增速加权排序，去掉重复与营销刷量。名次会随讨论变化上下浮动，凌晨与晚间是两个明显的高峰。
            </p>
          </div>
          <Link
            to="/rank"
            className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            查看完整榜单 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <ol className="flex flex-col gap-2">
              {topics.map((topic, idx) => (
                <li key={topic.id}>
                  <Link
                    to={`/topic/${topic.id}`}
                    className="group grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-md border border-transparent bg-background-100 px-3 py-3.5 transition-colors hover:border-primary-200 hover:bg-background-50 md:gap-4 md:px-4"
                  >
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-md font-heading text-base font-bold ${
                        idx === 0
                          ? "bg-primary-500 text-background-50"
                          : idx === 1
                            ? "bg-accent-500 text-accent-950"
                            : idx === 2
                              ? "bg-secondary-500 text-background-50"
                              : "bg-background-300 text-foreground-700"
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                        {topic.title}
                      </span>
                      <span className="mt-2 flex items-center gap-2">
                        <span className="hidden h-1.5 w-full max-w-[240px] overflow-hidden rounded-full bg-background-300 md:block">
                          <span
                            className="block h-full rounded-full bg-primary-500"
                            style={{ width: `${Math.max(24, (topic.heat / maxHeat) * 100)}%` }}
                          ></span>
                        </span>
                        <span className="text-xs text-secondary-600">{topic.category}</span>
                        <span className="text-xs text-foreground-500">{topic.publishTime}</span>
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary-600 md:text-sm">
                      <i className="ri-fire-fill"></i>
                      {formatHeat(topic.heat)}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="flex flex-col gap-6 lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-price-tag-3-line text-accent-600"></i>
                热议关键词
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {KEYWORDS.map((k) => (
                  <Link
                    key={k}
                    to={`/rank?q=${encodeURIComponent(k)}`}
                    className="whitespace-nowrap rounded-full bg-accent-100 px-3 py-1.5 text-xs text-accent-900 transition-colors hover:bg-accent-200"
                  >
                    {k}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-900">
                <i className="ri-information-line"></i>
                榜单说明
              </h3>
              <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-secondary-800">
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-secondary-500"></i>
                  来源单一的内容会做降权处理，并在详情页标注「待核实」。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-secondary-500"></i>
                  本周榜更看重持续性，可过滤一日游话题。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-secondary-500"></i>
                  榜单每小时刷新一次，夜间会做一次当日回顾。
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}